import type {
  CognitoryAnswerCitationView,
  CognitoryAnswerConversationScope,
  CognitoryAnswerSupportDetails,
} from "../api/cognitoryAnswerClient";

export const APPROVED_MAX_CONVERSATION_RECENT_TURNS = 5 as const;

export type CognitoryConversationRecentTurn = {
  question: string;
  requestId?: string;
  citationDocumentIds: string[];
  scopeDocumentIds?: string[];
  scopeSource?: CognitoryAnswerConversationScope["scopeSource"];
};

export type CognitoryConversationContext = {
  recentTurns: CognitoryConversationRecentTurn[];
};

export type CognitoryAskThreadTurn = {
  id: string;
  question: string;
  kind: "answered" | "refused" | "error";
  answerText?: string;
  citations?: CognitoryAnswerCitationView[];
  supportDetails?: CognitoryAnswerSupportDetails;
  requestId: string | null;
  errorMessage?: string;
  authoritativeScope?: CognitoryAnswerConversationScope;
};

const STORAGE_PREFIX = "cognitory_ask_thread:";

export function cognitoryAskThreadStorageKey(cognitoryId: string): string {
  return `${STORAGE_PREFIX}${cognitoryId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCitationView(value: unknown): value is CognitoryAnswerCitationView {
  if (!isRecord(value)) return false;
  return (
    typeof value.citationLabel === "string" &&
    (value.documentId === null || typeof value.documentId === "string") &&
    (value.documentVersionId === null ||
      typeof value.documentVersionId === "string") &&
    (value.chunkId === null || typeof value.chunkId === "string") &&
    (value.sourcePath === null || typeof value.sourcePath === "string") &&
    (value.sourceCharStart === null ||
      typeof value.sourceCharStart === "number") &&
    (value.sourceCharEnd === null || typeof value.sourceCharEnd === "number") &&
    (value.evidenceSpanStart === null ||
      typeof value.evidenceSpanStart === "number") &&
    (value.evidenceSpanEnd === null ||
      typeof value.evidenceSpanEnd === "number") &&
    (value.verificationReference === null ||
      typeof value.verificationReference === "string")
  );
}

function isAuthoritativeScope(
  value: unknown,
): value is CognitoryAnswerConversationScope {
  if (!isRecord(value)) return false;
  if (
    value.scopeSource !== "explicit_document" &&
    value.scopeSource !== "active_subject" &&
    value.scopeSource !== "citation_scope"
  ) {
    return false;
  }
  if (!Array.isArray(value.scopeDocumentIds)) return false;
  return value.scopeDocumentIds.every(
    (documentId): documentId is string => typeof documentId === "string",
  );
}

function isValidTurn(value: unknown): value is CognitoryAskThreadTurn {
  if (!isRecord(value)) return false;
  if (typeof value.id !== "string" || typeof value.question !== "string") {
    return false;
  }
  if (
    value.kind !== "answered" &&
    value.kind !== "refused" &&
    value.kind !== "error"
  ) {
    return false;
  }
  if (value.requestId !== null && typeof value.requestId !== "string") {
    return false;
  }
  if (value.answerText !== undefined && typeof value.answerText !== "string") {
    return false;
  }
  if (
    value.errorMessage !== undefined &&
    typeof value.errorMessage !== "string"
  ) {
    return false;
  }
  if (value.citations !== undefined) {
    if (!Array.isArray(value.citations)) return false;
    if (!value.citations.every(isCitationView)) return false;
  }
  if (
    value.authoritativeScope !== undefined &&
    !isAuthoritativeScope(value.authoritativeScope)
  ) {
    return false;
  }
  return true;
}

export function loadCognitoryAskThread(
  cognitoryId: string,
): CognitoryAskThreadTurn[] {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(
      cognitoryAskThreadStorageKey(cognitoryId),
    );
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidTurn);
  } catch {
    return [];
  }
}

export function saveCognitoryAskThread(
  cognitoryId: string,
  turns: CognitoryAskThreadTurn[],
): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      cognitoryAskThreadStorageKey(cognitoryId),
      JSON.stringify(turns),
    );
  } catch {
    // Ignore quota / private-mode failures; thread remains in memory.
  }
}

export function createCognitoryAskThreadTurnId(): string {
  return `turn_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function citationDocumentIdsFromTurn(turn: CognitoryAskThreadTurn): string[] {
  return [
    ...new Set(
      (turn.citations ?? [])
        .map(citation => citation.documentId)
        .filter((documentId): documentId is string => Boolean(documentId)),
    ),
  ];
}

export function buildConversationContextFromThread(
  turns: readonly CognitoryAskThreadTurn[],
  maxTurns: number = APPROVED_MAX_CONVERSATION_RECENT_TURNS,
): CognitoryConversationContext {
  const recentTurns: CognitoryConversationRecentTurn[] = [];
  for (const turn of turns) {
    const citationDocumentIds = citationDocumentIdsFromTurn(turn);
    const scopeDocumentIds = turn.authoritativeScope?.scopeDocumentIds ?? [];
    const scopeSource = turn.authoritativeScope?.scopeSource;

    if (turn.kind === "answered" && citationDocumentIds.length > 0) {
      recentTurns.push({
        question: turn.question,
        ...(turn.requestId ? { requestId: turn.requestId } : {}),
        citationDocumentIds,
        ...(scopeDocumentIds.length > 0 ? { scopeDocumentIds } : {}),
        ...(scopeSource ? { scopeSource } : {}),
      });
      continue;
    }

    if (scopeDocumentIds.length > 0 && scopeSource) {
      recentTurns.push({
        question: turn.question,
        ...(turn.requestId ? { requestId: turn.requestId } : {}),
        citationDocumentIds: [],
        scopeDocumentIds,
        scopeSource,
      });
    }
  }
  return {
    recentTurns: recentTurns.slice(-maxTurns),
  };
}

export function clearAllCognitoryAskThreads(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage failures; authentication state remains authoritative.
  }
}
